import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Lazy-init: read persisted cart synchronously on first render so
  // pages like /checkout don't flash "empty cart" on a hard reload.
  const [cart, setCart] = useState(() => {
    try {
      const saved = localStorage.getItem('vinepass_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('vinepass_cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (event, ticketType, quantity) => {
    // Use functional updater so back-to-back calls in the same tick (e.g.
    // the EventDetailPage forEach that adds multiple ticket types in one
    // click) operate on the *latest* cart state instead of the stale
    // closure snapshot — fixes the bug where only the last selected
    // ticket type survived.
    setCart(prev => {
      const idx = prev.findIndex(
        item => item.event.event_id === event.event_id && item.ticketType.ticket_id === ticketType.ticket_id
      );
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [...prev, { event, ticketType, quantity }];
    });
  };

  const removeFromCart = (eventId, ticketTypeId) => {
    setCart(prev => prev.filter(
      item => !(item.event.event_id === eventId && item.ticketType.ticket_id === ticketTypeId)
    ));
  };

  const updateQuantity = (eventId, ticketTypeId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(eventId, ticketTypeId);
      return;
    }
    setCart(prev => prev.map(item =>
      item.event.event_id === eventId && item.ticketType.ticket_id === ticketTypeId
        ? { ...item, quantity }
        : item
    ));
  };

  const clearCart = () => {
    setCart([]);
  };

  const getTotal = () => {
    return cart.reduce((total, item) => total + (item.ticketType.price * item.quantity), 0);
  };

  const getItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart, getTotal, getItemCount }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;